import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { filterCatalogItems } from './CatalogFilter';

export default function ManifestPicker({ presets, value, onChange, disabled }) {
  const id = useId();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({});
  const [chosen, setChosen] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const title = (preset) => preset.name || preset.id;
  const selected = presets.find((preset) => preset.id === value);
  const options = filterCatalogItems(presets, query).slice()
    .sort((a, b) => title(a).localeCompare(title(b), undefined, { sensitivity: 'base' }));
  const close = () => { setOpen(false); setQuery(''); setActive(0); };
  const choose = (preset) => { close(); setChosen(true); onChange(preset.id); };
  useLayoutEffect(() => {
    if (!open) return undefined;
    const updatePosition = (event) => {
      if (event?.target instanceof Node && dropdownRef.current?.contains(event.target)) return;
      const rect = inputRef.current.getBoundingClientRect();
      const width = Math.min(rect.width, window.innerWidth - 16);
      const top = rect.bottom + 4;
      setPosition({ top, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width, maxHeight: Math.max(0, Math.min(360, window.innerHeight - top - 8)) });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);
  useLayoutEffect(() => {
    const dropdown = dropdownRef.current;
    const option = dropdown?.querySelector('.manifest-picker-option.active');
    if (!option) return;
    if (option.offsetTop < dropdown.scrollTop) dropdown.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > dropdown.scrollTop + dropdown.clientHeight) {
      dropdown.scrollTop = option.offsetTop + option.offsetHeight - dropdown.clientHeight;
    }
  }, [active, open, query]);
  return (
    <div className="manifest-picker">
      <label htmlFor={id}>Manifest preset</label>
      <div className="manifest-picker-control">
      <input ref={inputRef} id={id} role="combobox" type="text" autoComplete="off"
        className="create-node-architecture" disabled={disabled}
        aria-expanded={open} aria-controls={`${id}-options`} aria-autocomplete="list"
        aria-activedescendant={open && options[active] ? `${id}-option-${active}` : undefined}
        value={open ? query : (chosen && selected ? title(selected) : '')}
        placeholder="Choose or type a manifest"
        onFocus={() => { setOpen(true); setActive(0); }}
        onClick={() => setOpen(true)}
        onBlur={close}
        onChange={(event) => { setQuery(event.target.value); setActive(0); setOpen(true); }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            setActive((current) => !open ? 0 : Math.max(0, Math.min(options.length - 1, current + (event.key === 'ArrowDown' ? 1 : -1))));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            if (open && options[active]) choose(options[active]);
            else setOpen(true);
          } else if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            close();
          }
        }} />
      <button type="button" className="manifest-picker-toggle" disabled={disabled}
        aria-label={open ? 'Hide manifests' : 'Show manifests'} aria-expanded={open} aria-controls={`${id}-options`}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (open) close();
          else { inputRef.current?.focus(); setOpen(true); }
        }}><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button>
      </div>
      {open && createPortal(<div ref={dropdownRef} className="manifest-picker-dropdown" style={position}
        onMouseDown={(event) => event.stopPropagation()}>
        <div id={`${id}-options`} role="listbox" aria-label="Manifest presets">
          {options.map((preset, index) => (
            <div id={`${id}-option-${index}`} key={preset.id} role="option"
              aria-selected={preset.id === value}
              className={`manifest-picker-option${index === active ? ' active' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onMouseMove={() => setActive(index)} onClick={() => choose(preset)}>
              {title(preset)}
            </div>
          ))}
        </div>
        {!options.length && <p role="status">No manifests match your filter.</p>}
      </div>, document.body)}
    </div>
  );
}
